'use client';

/**
 * Arena Review Canvas (B2 + B5 + B8). THROUGHPUT-FIRST.
 *
 * BLIND INTEGRITY: this component only ever receives `BlindGenerationView`
 * objects (opaque variantKey "A"/"B"/…, artifacts only — no model / provider /
 * label / prompt / config). The parent fetches with `blindMode=true`; the type
 * makes leaking identity a compile error. The leak test asserts the rendered
 * HTML contains no model/provider strings.
 *
 * Two switchable modes:
 *  - Pairwise: two candidates A/B side by side. Keys 1 = A wins, 2 = B wins,
 *    s = skip, c = toggle feedback drawer. Auto-advance immediately on a vote.
 *  - Pick: grid of all candidates for one sampleIndex; click / number-key to
 *    pick best; auto-advance.
 *
 * PAIR SCHEDULER (documented): reviewable generations are grouped by
 * sampleIndex; within each sampleIndex every UNORDERED pair of distinct
 * variantKeys becomes one comparison, canonicalized so the lexicographically
 * smaller variantKey is "A" (generationId) and the larger is "B" (opponent) —
 * so (A,B) and (B,A) are never both scheduled. The per-sampleIndex pair lists
 * are then interleaved ROUND-ROBIN across sampleIndexes (1st pair of each
 * sample, then 2nd pair of each, …) so a reviewer doesn't fatigue on one input.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CaretDown, CaretUp, SkipForward, Trophy } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import type { ArenaModality } from '@/lib/arena/types';
import type { BlindGenerationView } from './constants';
import { DEFECT_TAGS, RUBRIC_DIMENSIONS, isReviewable } from './constants';
import { GenerationMedia, CardMetrics, ZoomOverlay } from './generation-media';

type ReviewMode = 'pairwise' | 'pick';

interface PairTask {
  sampleIndex: number;
  a: BlindGenerationView; // canonical first (smaller variantKey)
  b: BlindGenerationView;
}

// --- pure schedulers (exported for tests) ---------------------------------
export function buildPairTasks(gens: BlindGenerationView[]): PairTask[] {
  const bySample = new Map<number, BlindGenerationView[]>();
  for (const g of gens) {
    if (!isReviewable(g)) continue;
    const arr = bySample.get(g.sampleIndex) ?? [];
    arr.push(g);
    bySample.set(g.sampleIndex, arr);
  }
  const perSample: PairTask[][] = [];
  const sampleIndexes = [...bySample.keys()].sort((x, y) => x - y);
  for (const si of sampleIndexes) {
    const items = [...(bySample.get(si) ?? [])].sort(cmpKey);
    const pairs: PairTask[] = [];
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        // Only distinct variant keys make a meaningful comparison.
        if (items[i].variantKey === items[j].variantKey) continue;
        pairs.push({ sampleIndex: si, a: items[i], b: items[j] });
      }
    }
    if (pairs.length) perSample.push(pairs);
  }
  // Round-robin interleave across sampleIndexes.
  const out: PairTask[] = [];
  const maxLen = Math.max(0, ...perSample.map((p) => p.length));
  for (let round = 0; round < maxLen; round++) {
    for (const pairs of perSample) {
      if (round < pairs.length) out.push(pairs[round]);
    }
  }
  return out;
}

interface PickTask {
  sampleIndex: number;
  candidates: BlindGenerationView[];
}

export function buildPickTasks(gens: BlindGenerationView[]): PickTask[] {
  const bySample = new Map<number, BlindGenerationView[]>();
  for (const g of gens) {
    if (!isReviewable(g)) continue;
    const arr = bySample.get(g.sampleIndex) ?? [];
    arr.push(g);
    bySample.set(g.sampleIndex, arr);
  }
  return [...bySample.keys()]
    .sort((x, y) => x - y)
    .map((si) => ({ sampleIndex: si, candidates: [...(bySample.get(si) ?? [])].sort(cmpKey) }))
    .filter((t) => t.candidates.length >= 2);
}

function cmpKey(a: BlindGenerationView, b: BlindGenerationView): number {
  if (a.variantKey !== b.variantKey) return a.variantKey < b.variantKey ? -1 : 1;
  return a.id < b.id ? -1 : 1;
}

interface Feedback {
  comment: string;
  tags: string[];
  rubric: Record<string, number>;
}

function emptyFeedback(): Feedback {
  return { comment: '', tags: [], rubric: {} };
}

function feedbackPayload(fb: Feedback) {
  const out: { comment?: string; tags?: string[]; rubricScores?: Record<string, number> } = {};
  if (fb.comment.trim()) out.comment = fb.comment.trim();
  if (fb.tags.length) out.tags = fb.tags;
  if (Object.keys(fb.rubric).length) out.rubricScores = fb.rubric;
  return out;
}

export function ReviewCanvas({
  experimentId,
  modality,
  generations,
}: {
  experimentId: string;
  modality: ArenaModality;
  generations: BlindGenerationView[];
}) {
  const [mode, setMode] = useState<ReviewMode>('pairwise');
  const [cursor, setCursor] = useState(0);
  const [streak, setStreak] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(emptyFeedback);
  const [zoom, setZoom] = useState<string | null>(null);

  const pairTasks = useMemo(() => buildPairTasks(generations), [generations]);
  const pickTasks = useMemo(() => buildPickTasks(generations), [generations]);
  const tasks = mode === 'pairwise' ? pairTasks : pickTasks;

  // Reset cursor/feedback when switching mode or the task set changes shape.
  useEffect(() => {
    setCursor(0);
    setFeedback(emptyFeedback());
  }, [mode]);

  const feedbackRef = useRef(feedback);
  feedbackRef.current = feedback;

  const advance = useCallback(() => {
    setCursor((c) => c + 1);
    setFeedback(emptyFeedback());
    setDrawerOpen(false);
  }, []);

  const post = useCallback(
    async (payload: Record<string, unknown>) => {
      try {
        const res = await fetch(`/api/arena/experiments/${experimentId}/reviews`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.status === 409) return; // duplicate — silently move on
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(typeof data?.error === 'string' ? data.error : `Review failed (${res.status})`);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Review failed');
      }
    },
    [experimentId],
  );

  const votePairwise = useCallback(
    (winner: 'a' | 'b') => {
      const task = pairTasks[cursor];
      if (!task) return;
      const payload = {
        generationId: task.a.id,
        mode: 'pairwise' as const,
        opponentGenerationId: task.b.id,
        won: winner === 'a',
        ...feedbackPayload(feedbackRef.current),
      };
      setStreak((s) => s + 1);
      advance();
      void post(payload);
    },
    [pairTasks, cursor, advance, post],
  );

  const votePick = useCallback(
    (candidate: BlindGenerationView) => {
      const payload = {
        generationId: candidate.id,
        mode: 'pick' as const,
        ...feedbackPayload(feedbackRef.current),
      };
      setStreak((s) => s + 1);
      advance();
      void post(payload);
    },
    [advance, post],
  );

  const skip = useCallback(() => advance(), [advance]);

  // Keyboard map (B8). Ignored while typing in an input/textarea.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === 'c') {
        e.preventDefault();
        setDrawerOpen((d) => !d);
        return;
      }
      if (key === 's') {
        e.preventDefault();
        skip();
        return;
      }
      if (mode === 'pairwise') {
        if (key === '1') {
          e.preventDefault();
          votePairwise('a');
        } else if (key === '2') {
          e.preventDefault();
          votePairwise('b');
        }
      } else {
        const n = Number.parseInt(key, 10);
        if (!Number.isNaN(n) && n >= 1) {
          const task = pickTasks[cursor];
          const cand = task?.candidates[n - 1];
          if (cand) {
            e.preventDefault();
            votePick(cand);
          }
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, cursor, pickTasks, votePairwise, votePick, skip]);

  const done = cursor >= tasks.length;
  const rubricDims = RUBRIC_DIMENSIONS[modality] ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="arena-review-canvas">
      <ZoomOverlay url={zoom} onClose={() => setZoom(null)} />
      {/* Header: mode switch + streak */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex overflow-hidden rounded-full border border-border/60">
          {(['pairwise', 'pick'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                'px-3 py-1 text-xs font-semibold capitalize transition-colors',
                mode === m ? 'bg-violet-500 text-white' : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`arena-mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 font-semibold text-foreground">
            <Trophy weight="duotone" className="size-4 text-amber-400" />
            <span data-testid="arena-streak">{streak}</span> reviewed
          </span>
          <span>
            {Math.min(cursor + (done ? 0 : 1), tasks.length)} / {tasks.length}
          </span>
        </div>
      </div>

      {done ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-border/60 bg-card/40 text-center">
          <Trophy weight="duotone" className="size-9 text-amber-400" />
          <p className="mt-3 text-base font-semibold">All caught up</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            {tasks.length === 0
              ? 'No reviewable candidates yet. Run the experiment (or wait for generations to succeed), then come back.'
              : `You reviewed ${streak} item${streak === 1 ? '' : 's'} this session.`}
          </p>
        </div>
      ) : mode === 'pairwise' ? (
        <PairwiseView
          task={pairTasks[cursor]}
          onVote={votePairwise}
          onSkip={skip}
          onZoom={setZoom}
        />
      ) : (
        <PickView task={pickTasks[cursor]} onPick={votePick} onSkip={skip} onZoom={setZoom} />
      )}

      {!done && (
        <FeedbackDrawer
          open={drawerOpen}
          onToggle={() => setDrawerOpen((d) => !d)}
          feedback={feedback}
          setFeedback={setFeedback}
          rubricDims={rubricDims}
        />
      )}
    </div>
  );
}

function PairwiseView({
  task,
  onVote,
  onSkip,
  onZoom,
}: {
  task: PairTask | undefined;
  onVote: (w: 'a' | 'b') => void;
  onSkip: () => void;
  onZoom: (url: string) => void;
}) {
  if (!task) return null;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {(['a', 'b'] as const).map((side) => {
        const gen = task[side];
        return (
          <button
            key={gen.id}
            type="button"
            onClick={() => onVote(side)}
            className="group flex flex-col gap-2 rounded-2xl border border-border/60 bg-card/40 p-3 text-left transition-colors hover:border-violet-500/50"
            data-testid={`arena-vote-${side}`}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex size-6 items-center justify-center rounded-full bg-muted text-xs font-bold">
                {side === 'a' ? '1' : '2'}
              </span>
              <CardMetrics gen={gen} />
            </div>
            <GenerationMedia gen={gen} onZoom={onZoom} />
            <span className="text-center text-[11px] font-medium text-muted-foreground group-hover:text-violet-300">
              {side === 'a' ? 'Press 1 — this wins' : 'Press 2 — this wins'}
            </span>
          </button>
        );
      })}
      <div className="md:col-span-2">
        <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={onSkip}>
          <SkipForward weight="bold" className="mr-1 size-3.5" /> Skip (s)
        </Button>
      </div>
    </div>
  );
}

function PickView({
  task,
  onPick,
  onSkip,
  onZoom,
}: {
  task: PickTask | undefined;
  onPick: (g: BlindGenerationView) => void;
  onSkip: () => void;
  onZoom: (url: string) => void;
}) {
  if (!task) return null;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Sample #{task.sampleIndex} — pick the best (click or number key).
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
        {task.candidates.map((gen, i) => (
          <button
            key={gen.id}
            type="button"
            onClick={() => onPick(gen)}
            className="group flex flex-col gap-2 rounded-2xl border border-border/60 bg-card/40 p-2.5 text-left transition-colors hover:border-violet-500/50"
            data-testid={`arena-pick-${i}`}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex size-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold">
                {i + 1}
              </span>
              <CardMetrics gen={gen} />
            </div>
            <GenerationMedia gen={gen} onZoom={onZoom} />
          </button>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={onSkip}>
        <SkipForward weight="bold" className="mr-1 size-3.5" /> Skip (s)
      </Button>
    </div>
  );
}

function FeedbackDrawer({
  open,
  onToggle,
  feedback,
  setFeedback,
  rubricDims,
}: {
  open: boolean;
  onToggle: () => void;
  feedback: Feedback;
  setFeedback: (f: Feedback) => void;
  rubricDims: string[];
}) {
  const toggleTag = (tag: string) => {
    const has = feedback.tags.includes(tag);
    setFeedback({ ...feedback, tags: has ? feedback.tags.filter((t) => t !== tag) : [...feedback.tags, tag] });
  };
  return (
    <div className="rounded-2xl border border-border/60 bg-card/30">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        data-testid="arena-feedback-toggle"
      >
        <span>Optional feedback (c)</span>
        {open ? <CaretUp weight="bold" className="size-3.5" /> : <CaretDown weight="bold" className="size-3.5" />}
      </button>
      {open && (
        <div className="space-y-4 border-t border-border/60 p-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold text-muted-foreground">Comment</label>
            <Textarea
              value={feedback.comment}
              onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
              placeholder="What's good or bad? (optional)"
              rows={2}
              className="text-sm"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold text-muted-foreground">Defect tags</label>
            <div className="flex flex-wrap gap-1.5">
              {DEFECT_TAGS.map((tag) => {
                const active = feedback.tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors',
                      active
                        ? 'border-red-500/40 bg-red-500/10 text-red-300'
                        : 'border-border/60 text-muted-foreground hover:text-foreground',
                    )}
                    data-testid={`arena-tag-${tag}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
          {rubricDims.length > 0 && (
            <div className="space-y-3">
              <label className="block text-[11px] font-semibold text-muted-foreground">Rubric (1–5)</label>
              {rubricDims.map((dim) => {
                const val = feedback.rubric[dim] ?? 3;
                return (
                  <div key={dim} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 text-[11px] capitalize text-muted-foreground">{dim}</span>
                    <Slider
                      min={1}
                      max={5}
                      step={1}
                      value={[val]}
                      onValueChange={([v]) => setFeedback({ ...feedback, rubric: { ...feedback.rubric, [dim]: v } })}
                      className="flex-1"
                    />
                    <span className="w-4 text-right text-xs font-semibold tabular-nums">{val}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
